<template>
  <template v-if="isLoading">
    <AppContentLoader />
  </template>

  <template v-else-if="hasError">
    <h1 class="text-center mt-5">
      Unable to load plan. Make sure the URL is correct.
    </h1>
  </template>

  <template v-else>
    <PublishedPlanBanner :plan="plan.name" />

    <PlanDocument
      :frameworks="frameworks"
      :plan="plan"
      :nodes="planStore.nodes"
      :edges="planStore.edges"
    />
  </template>
</template>

<script setup>
import { HttpStatusCode } from 'axios';
import { DataSet } from 'vis-data';
import { computed, onBeforeMount, ref } from 'vue';
import { useRoute } from 'vue-router';

import { AppContentLoader } from '@/components/app';
import { PublishedPlanBanner } from '@/components/banners';
import { PlanDocument } from '@/components/plan';

import { usePlanStore } from '@/store';
import { useHttp } from '@/composables';

const planStore = usePlanStore();
const route = useRoute();

const frameworks = ref();
const isLoading = ref(true);
const plan = computed(() => {
  return {
    id: planStore.id,
    name: planStore.name,
    description: planStore.description,
    introduction: planStore.introduction,
    executiveSummary: planStore.executiveSummary,
    conclusion: planStore.conclusion,
    vision: planStore.vision,
    nodes: planStore.nodes,
    edges: planStore.edges,
    lastPublishedAt: planStore.lastPublishedAt,
    logo: planStore.logo,
    district: planStore.district,
  };
});
const hasError = ref(false);

onBeforeMount(async () => {
  await Promise.all([
    loadPlan(),
    loadFrameworks(),
  ]);

  isLoading.value = false;
});

const loadPlan = async () => {
  const response = await useHttp().get(`/anonymous/plans/${route.params.planIdentifier}`);

  if (response.statusCode === HttpStatusCode.Ok) {
    let { id, name, description, introduction, executiveSummary, conclusion, nodes, edges, vision, logo, district, lastPublishedAt } = response.data;

    nodes = new DataSet(nodes || []);
    edges = new DataSet(edges || []);

    planStore.$patch({
      id, name, description, vision, introduction, executiveSummary, conclusion, nodes, edges, lastPublishedAt, logo, district,
    });
  } else {
    hasError.value = true;
  }

  planStore.loading = false;
};

const loadFrameworks = async () => {
  const response = await useHttp().get('/anonymous/frameworks');

  if (response.statusCode === HttpStatusCode.Ok) {
    frameworks.value = response.data.items;
  } else {
    hasError.value = true;
  }
};
</script>
